import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Play, Pause, ArrowLeft, Clock, CheckCircle, Volume2, Lock, ShoppingCart } from 'lucide-react';
import ShareButtons from '../components/ShareButtons';

export default function MeditationPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { currentColors } = useTheme();
  const [meditation, setMeditation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [accessInfo, setAccessInfo] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const audioRef = useRef(null);

  useEffect(() => {
    loadMeditation();
    checkAccess();
  }, [id]);

  const loadMeditation = async () => {
    try {
      const response = await axios.get(`${API}/meditations/${id}`);
      setMeditation(response.data);
    } catch (error) {
      console.error('Failed to load meditation:', error);
    }
  };

  const checkAccess = async () => {
    if (!token) {
      setAccessInfo({ has_access: false, message: 'Please log in to access this content' });
      setCheckingAccess(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/meditations/${id}/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessInfo(response.data);
    } catch (error) {
      console.error('Failed to check access:', error);
      // Default to no access on error for premium content
      setAccessInfo({ has_access: false, message: 'Unable to verify access' });
    } finally {
      setCheckingAccess(false);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setAudioProgress(audioRef.current.currentTime);
      setAudioDuration(audioRef.current.duration || 0);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleComplete = async () => {
    try {
      await axios.post(
        `${API}/progress`,
        {
          activity_type: 'meditation',
          activity_id: meditation.id,
          duration: meditation.duration
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompleted(true);
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  // Get the audio URL with proper base
  const getAudioUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    // For relative URLs like /api/uploads/audio/...
    const baseUrl = API.replace('/api', '');
    return `${baseUrl}${url}`;
  };

  if (!meditation) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 rounded-full animate-spin" 
             style={{ borderColor: `${currentColors?.primary}30`, borderTopColor: currentColors?.primary }}></div>
      </div>
    );
  }

  // Show access restriction for premium content
  const showAccessRestriction = meditation.is_premium && accessInfo && !accessInfo.has_access;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="meditation-player">
      <button
        onClick={() => navigate('/meditations')}
        className="flex items-center space-x-2 transition-all hover:opacity-80"
        style={{ color: currentColors.primary }}
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Back to Meditations</span>
      </button>

      {/* Premium Content Access Restriction */}
      {showAccessRestriction && (
        <div 
          className="glass-card p-8 text-center"
          style={{ borderColor: `${currentColors.accent}50` }}
        >
          <Lock className="w-16 h-16 mx-auto mb-4" style={{ color: currentColors.accent }} />
          <h2 className="text-2xl font-cinzel mb-2" style={{ color: currentColors.primary }}>
            Premium Content
          </h2>
          <p className="mb-6" style={{ color: currentColors.textMuted }}>
            {accessInfo.message || 'This meditation requires premium access.'}
          </p>
          
          {accessInfo.prices && (
            <div className="mb-6">
              <p className="text-sm mb-3" style={{ color: currentColors.text }}>Starting from:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {Object.entries(accessInfo.prices).filter(([_, price]) => price > 0).slice(0, 3).map(([currency, price]) => (
                  <span 
                    key={currency}
                    className="px-4 py-2 rounded-full text-sm font-medium"
                    style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
                  >
                    {currency} {price}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: currentColors.primary, color: '#fff' }}
            >
              <ShoppingCart className="w-5 h-5" />
              Get Premium Access
            </Link>
            <button
              onClick={() => navigate('/meditations')}
              className="px-6 py-3 rounded-lg font-medium transition-all"
              style={{ 
                backgroundColor: `${currentColors.textMuted}20`, 
                color: currentColors.text 
              }}
            >
              Browse Free Meditations
            </button>
          </div>
        </div>
      )}

      {/* Main Content - Only show if user has access or content is free */}
      {(!meditation.is_premium || (accessInfo && accessInfo.has_access)) && (
        <>
      <div className="glass-card overflow-hidden">
        {/* Hero Image */}
        <div className="h-64 relative flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${currentColors.primary}40, ${currentColors.secondary}40)` }}>
          {meditation.image_url ? (
            <img src={meditation.image_url} alt={meditation.title} className="w-full h-full object-cover" />
          ) : (
            <div className="text-8xl">🧘</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute bottom-4 left-6 right-6">
            <span 
              className="px-3 py-1 text-sm rounded-full"
              style={{ backgroundColor: `${currentColors.primary}40`, color: currentColors.primary }}
            >
              {meditation.category}
            </span>
          </div>
        </div>

        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h1 className="text-2xl md:text-3xl font-cinzel" style={{ color: currentColors.primary }}>
              {meditation.title}
            </h1>
            <div className="flex items-center gap-4">
              <div className="flex items-center" style={{ color: currentColors.textMuted }}>
                <Clock className="w-5 h-5 mr-2" />
                {meditation.duration} minutes
              </div>
              <ShareButtons 
                contentType="meditation"
                contentId={meditation.id}
                title={meditation.title}
                description={meditation.description}
              />
            </div>
          </div>
          
          <p className="mb-6" style={{ color: currentColors.textMuted }}>{meditation.description}</p>

          {/* Audio Player */}
          {meditation.audio_url && (
            <div 
              className="rounded-xl p-6 mb-6"
              style={{ backgroundColor: `${currentColors.primary}10`, border: `1px solid ${currentColors.primary}30` }}
            >
              <div className="flex items-center gap-2 mb-4" style={{ color: currentColors.primary }}>
                <Volume2 className="w-5 h-5" />
                <span className="font-medium">Audio Guide</span>
              </div>
              <audio
                ref={audioRef}
                src={getAudioUrl(meditation.audio_url)}
                onEnded={() => setIsPlaying(false)}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleTimeUpdate}
              />
              <div className="flex items-center gap-4">
                <button
                  onClick={handlePlayPause}
                  className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-105"
                  style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                  data-testid="play-pause-button"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6" />
                  ) : (
                    <Play className="w-6 h-6 ml-1" />
                  )}
                </button>
                <div className="flex-1">
                  <div 
                    className="h-2 rounded-full overflow-hidden"
                    style={{ backgroundColor: `${currentColors.primary}20` }}
                  >
                    <div 
                      className="h-full transition-all"
                      style={{ 
                        width: audioDuration ? `${(audioProgress / audioDuration) * 100}%` : '0%',
                        backgroundColor: currentColors.primary
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs mt-1" style={{ color: currentColors.textMuted }}>
                    <span>{formatTime(audioProgress)}</span>
                    <span>{formatTime(audioDuration)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content - Custom HTML or Script Text */}
          <div 
            className="rounded-xl p-6 mb-6"
            style={{ backgroundColor: `${currentColors.secondary}10`, border: `1px solid ${currentColors.border}` }}
          >
            <h2 className="text-xl font-cinzel mb-4" style={{ color: currentColors.text }}>
              {meditation.use_custom_html ? 'Guided Meditation' : 'Meditation Script'}
            </h2>
            
            {meditation.use_custom_html && meditation.custom_html ? (
              <div 
                className="meditation-custom-content prose prose-invert max-w-none"
                style={{ color: currentColors.text }}
                dangerouslySetInnerHTML={{ __html: meditation.custom_html }}
              />
            ) : (
              <p className="leading-relaxed whitespace-pre-wrap" style={{ color: currentColors.text }}>
                {meditation.script_text}
              </p>
            )}
          </div>

          {/* Complete Button */}
          {completed ? (
            <div 
              className="flex items-center justify-center space-x-2 py-4 rounded-lg" 
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}
              data-testid="completed-message"
            >
              <CheckCircle className="w-6 h-6" />
              <span className="font-medium">Session completed! Great job!</span>
            </div>
          ) : (
            <button
              onClick={handleComplete}
              className="cosmic-btn w-full py-4"
              data-testid="complete-button"
            >
              Mark as Completed
            </button>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
}
